import { useMutation, useQuery } from "@tanstack/react-query"
import { reportsService, REPORTS_ENDPOINTS } from "services/reportsService"
import { commonService, COMMON_ENDPOINTS } from "services/commonService"
import { IPreviewRequest } from "../interface/preview-request"
import { onQueryError } from "utils/utils"

export interface IEmployeeScope {
  id: number
  text: string
}

export interface IGetDateRange {
  id: number
  text: string
}
export interface IGetGroupingTypes {
  id: number
  text: string
}

export interface IReportViewPreview {
  range: string
  grouping: string
  report_date: string
  report_download_title: string
  payrollItems?: IReportViewPreviewPayrollItem[] | null
  payrollEarnings?: IReportViewPreviewPayrollEarning[] | null
  tempPayrollItems?: null
  employeeBenefits?: IReportViewPreviewEmployeeBenefit[] | null
  employeeRequests?: IReportViewPreviewEmployeeRequest[] | null
  employeeLeaveBalances?: null
  payrollChanges?: IReportViewPreviewPayrollChange[] | null
  payrollExemptions?: IReportViewPreviewPayrollExemption[] | null
  customFields?: IReportViewPreviewCustomField[] | null
  employeeCustomFields?: IReportViewPreviewEmployeeCustomField[] | null
  employeeSummary?: null
  officeAddress: IReportViewPreviewOfficeAddress
  persons?: null
  homeAddresses?: null
  nextofKins?: null
  payrollReportSummaries?: IReportViewPreviewSummary[] | null
  payrollReportSummaryValues?: IReportViewPreviewSummaryValue[] | null
  payrollReportSummaryAllowances?: IReportViewPreviewSummaryAllowance[] | null
  payrollReportSummaryEarnings?: IReportViewPreviewSummaryEarning[] | null
  statutoryBenefits?: IReportViewPreviewStatutoryBenefitsEntity[] | null
  reviewRound?: null
  paygradeReportSummaries?: null
  paygradeReportSummaryAllowances?: null[] | null
  paygradeReportSummaryStatutoryBenefits?: null
  paygradeReportSummaryTaxReliefs?: null
  paygradeReportSummaryValues?: IReportViewPreviewPayScaleSummaryValue[] | null
  paybandReportSummaries?: null
  paybandReportSummaryAllowances?: null
  paybandReportSummaryStatutoryBenefits?: null
  paybandReportSummaryTaxReliefs?: null
  paybandReportSummaryValues?: IReportViewPreviewPayScaleSummaryValue[] | null
  pensionSchedules?: null
  taxSchedules?: null
  nhfSchedules?: null
  nsitfSchedules?: null
  itfSchedules?: null
  earnedWageWithdrawals?: null
  organisationAvatarFilename?: null
  organisationName?: null
  logoUploaded: boolean
  colspan: number
  qualifiedPayrollDate?: string | null
  payPeriod?: string | null
  payrollStatusId?: null
  employeeEarnings: boolean
  employeeTaxes: boolean
  deductions: boolean
  contributions: boolean
  totals: boolean
  customInfo: boolean
  employeeTitle: boolean
  hireDate: boolean
  department: boolean
  location: boolean
  gender: boolean
  daysWorked: boolean
  pension: boolean
  nhis: boolean
  nhf: boolean
  lifeAssurance: boolean
  currentYear: number
  employeeNumber: boolean
  paygrade: boolean
  benefit?: null
  alias: boolean
  routineAnnualGross: boolean
  monthlyGross: boolean
  routineMonthlyGross: boolean
  annualNet: boolean
  routineAnnualNet: boolean
  routineMonthlyNet: boolean
  annualAllowances: boolean
  annualStatutoryBenefits: boolean
  annualTaxReliefs: boolean
  annualTax: boolean
  createdDate: boolean
  employeeRequestTypeId: number
  liability: boolean
  pensionReferenceNumber?: null
  taxIdentificationNumber?: null
  housingReferenceNumber?: null
  nsitfReferenceNumber?: null
  itfReferenceNumber?: null
  paymentScheduleItemStatusId: number
  scheduleDate?: null
  consolidationGrouping?: "year" | "month"
}
export interface IReportViewPreviewOfficeAddress {
  office_address_key?: null
  office_address_id: number
  address_line_1: string
  address_line_2: string
  address_city: string
  address_state: string
  state_code: string
  country: string
  country_code: string
  zip_code: string
  phone: string
  display_name: string
  full_address?: null
  tax_office_name?: null
  tax_office_code?: null
  housing_location_name?: null
  housing_location_code?: null
  latitude?: null
  longitude?: null
  tax_id?: null
  has_children?: null
  is_deleted: boolean
  is_active: boolean
}
export interface IReportViewPreviewStatutoryBenefitsEntity {
  statutory_benefit_key?: null
  statutory_benefit_id: number
  statutory_benefit_type_id?: null
  can_reset?: null
  description: string
  is_deleted: boolean
  is_active: boolean
}
export interface IReportViewPreviewSummary {
  description: string
  person_id: string
  payroll_id: string
  payroll_month?: string | null
  pay_period?: string | null
  identification_number: number
  full_name: string
  nick_name: string
  first_name: string
  middle_initial: string
  last_name: string
  department: string
  employee_title: string
  hire_date: string
  gender: string
  paygrade: string
  office_display_name: string
  gross: number
  payable: number
  deduction: number
  tax: number
  net: number
  last_gross: number
  last_payable: number
  last_deduction: number
  last_tax?: number | null
  last_net?: number | null
  pension_deduction?: number | null
  pension_contribution?: number | null
  housing_deduction?: number | null
  housing_contribution?: number | null
  health_insurance_deduction?: number | null
  health_insurance_contribution?: number | null
  life_assurance_deduction?: number | null
  life_assurance_contribution?: number | null
  benefit_deduction?: number | null
  benefit_contribution?: number | null
  hmo_deduction?: number | null
  other_deduction?: number | null
  other_contribution?: number | null
  total_days: number
  days_worked: number
  currency?: string | null
  currency_code?: string | null
  currency_symbol: string
}
export interface IReportViewPreviewSummaryValue {
  has_pension_deduction: boolean
  has_pension_contribution: boolean
  has_housing_deduction: boolean
  has_housing_contribution: boolean
  has_health_insurance_deduction: boolean
  has_health_insurance_contribution: boolean
  has_life_assurance_deduction: boolean
  has_life_assurance_contribution: boolean
  has_benefit_deduction: boolean
  has_benefit_contribution: boolean
  has_hmo_deduction: boolean
  has_other_deduction: boolean
  has_other_contribution: boolean
}
export interface IReportViewPreviewSummaryAllowance {
  description: string
}
export interface IReportViewPreviewSummaryEarning {
  person_id: string
  payroll_id: string
  description: string
  amount: number
  currency_symbol?: string | null
}
export interface IReportViewPreviewPayrollItem {
  payroll_item_key?: null
  payroll_item_id: number
  parent_payroll_item_id?: null
  payroll_key?: null
  payroll_id: number
  payroll_type_id?: null
  payroll_type?: null
  payroll_schedule_id?: null
  payroll_schedule?: null
  payroll_item_type_id: number
  payroll_item_type?: null
  payroll_status_id?: null
  payroll_status?: null
  payroll_item_status_id?: null
  payroll_item_status?: null
  entity_type_id?: null
  entity_type?: null
  paygrade_id: number
  paygrade: string
  annual_gross?: null
  employee_request_id?: null
  payroll_change_id?: null
  payment_reversal_id?: null
  description?: string | null
  alias?: string | null
  person_key?: null
  person_id: number
  first_name: string
  middle_initial?: string | null
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  employee_title?: string | null
  identification_number?: string | null
  department: string
  office_address_id?: number | null
  office_display_name?: string | null
  office_address_phone?: string | null
  total_days: number
  days_worked: number
  actual_amount?: null
  amount: number
  is_taxable?: null
  tax_type_id?: null
  effective_tax_rate?: null
  tax_amount?: number | null
  after_tax_amount?: number | null
  deducted_amount?: number | null
  cummulative_deducted_amount?: null
  avatar_url?: null
  pay_period: string
  start_date: string
  cut_off_date: string
  payroll_month: string
  pay_date: string
  payment_date?: null
  is_deleted: boolean
  is_active: boolean
  dateRange?: null
  document_download_title?: null
  currency?: null
  currency_code?: null
  currency_symbol: string
}
export interface IReportViewPreviewPayrollEarning {
  payroll_earning_key?: null
  payroll_earning_id: number
  payroll_id: number
  pay_period: string
  payroll_change_id: number
  amount: number
  tax_amount: number
  after_tax_amount: number
  currency_code?: null
  currency_symbol: string
  description: string
  person_id: number
  first_name: string
  middle_initial: string
  last_name: string
  full_name: string
  nick_name: string
  email?: null
  identification_number: string
  employee_title_id?: null
  employee_title: string
  department_id?: null
  department: string
  office_address_id: number
  office_display_name?: null
  avatar_url?: null
  is_deleted: boolean
  is_active: boolean
}
export interface IReportViewPreviewEmployeeBenefit {
  person_id: number
  full_name: string
  identification_number?: string | null
  employee_title?: string | null
  department?: string | null
  office_address_id?: number | null
  office_display_name?: string | null
  description?: string | null
  external_deduction?: number | null
  external_contribution?: number | null
  currency_symbol?: string | null
}
export interface IReportViewPreviewEmployeeRequest {
  employee_request_id: number
  employee_request_type_id?: number | null
  employee_request_status?: string | null
  person_id: number
  full_name: string
  identification_number?: string | null
  employee_title?: string | null
  department?: string | null
  office_display_name?: string | null
  policy_id?: number | null
  leave_policy?: string | null
  description?: string | null
  reason?: string | null
  working_days?: number | null
  amount?: number | null
  repay_period?: number | null
  repay_interval?: string | null
  start_date?: string | null
  stop_date?: string | null
  friendly_date?: string | null
  created_date?: string | null
  currency_symbol?: string | null
}
export interface IReportViewPreviewPayrollChange {
  payroll_change_key?: null
  payroll_change_id: number
  payroll_change_type_id: number
  payroll_change_type: string
  payroll_change_option_id?: null
  payroll_change_option?: null
  payroll_change_frequency_id: number
  payroll_change_frequency: string
  repeat_period: number
  repeat_interval_id: number
  repeat_interval: string
  payroll_schedule_id: number
  payroll_schedule: string
  payroll_schedule_alias: string
  payroll_remittance_id?: null
  payroll_remittance?: null
  payroll_remittance_alias?: null
  payable_type_unit_id?: null
  payable_type_unit?: null
  units?: null
  unit_rate?: null
  start_date: string
  stop_date: string
  created_date: string
  group_payband_id?: null
  group_payband?: null
  group_paygrade_id?: null
  group_paygrade?: null
  entity_name: string
  person_key?: null
  person_id: number
  first_name?: null
  middle_initial?: null
  last_name?: null
  full_name: string
  nick_name: string
  identification_number: string
  email?: null
  employee_title: string
  department: string
  office_display_name: string
  hire_date: string
  gender: string
  person_status_id?: null
  person_status?: null
  paygrade_id?: null
  paygrade: string
  basis_id?: null
  entity_type: "Employee" | "Paygrade" | "Payband"
  basis?: null
  value?: null
  amount: number
  tax_amount: number
  after_tax_amount: number
  effective_tax_rate: number
  is_taxable?: null
  tax_mode?: string
  tax_mode_id?: number
  has_been_used: boolean
  is_for_payroll?: null
  note?: string | null
  currency?: null
  currency_code?: null
  currency_symbol: string
  is_deleted?: boolean
  is_active?: boolean
}
export interface IReportViewPreviewPayrollExemption {
  payroll_exemption_id?: number | null
  employee_request_id?: number | null
  payroll_change_frequency_id?: number | null
  person_id: number
  full_name: string
  identification_number?: string | null
  employee_title?: string | null
  paygrade?: string | null
  hire_date?: string | null
  department?: string | null
  note?: string | null
  start_date?: string | null
  stop_date?: string | null
}
export interface IReportViewPreviewPayScaleSummaryValue {
  paygrade_id?: number | null
  payband_id?: number | null
  allowance_id?: number | null
  benefit_id?: number | null
  relief_id?: number | null
  description?: string | null
}
export interface IReportViewPreviewCustomField {
  custom_field_id: string
  description: string
}
export interface IReportViewPreviewEmployeeCustomField {
  person_id: string
  custom_field_id: string
  description: string
}

export interface EmployeeTypes {
  employeeRequestTypeId: number
  description: string
  createdDate: string
  createdBy?: null
  updatedDate: string
  updatedBy?: null
  recordGuid: string
  recordVersion: number
  isDeleted: boolean
  isActive: boolean
  createdByName?: null
  updatedByName?: null
  employeeRequests?: null[] | null
}

export const useGetDateRanges = () => {
  const { data, isLoading } = useQuery<IBaseRes<IGetDateRange[]>>({
    queryKey: [REPORTS_ENDPOINTS.GET_DATE_RANGES],
    queryFn: async () => reportsService.getDateRanges(),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })

  return { dateRanges: data, isLoading }
}
export const useGetDatePeriods = (id: string) => {
  const { data, isLoading } = useQuery<IBaseRes<IGetGroupingTypes[]>>({
    enabled: !!id,
    queryKey: [REPORTS_ENDPOINTS.GET_DATE_PERIODS, id],
    queryFn: async () => reportsService.getDatePeriods(id),
  })

  return { periods: data, loadingPeriods: isLoading }
}
export const useGetGroupingTypes = () => {
  const { data, isLoading } = useQuery<IBaseRes<IGetGroupingTypes[]>>({
    queryKey: [REPORTS_ENDPOINTS.GET_GROUPING_TYPES],
    queryFn: async () => reportsService.getGroupingTypes(),
  })

  return { groups: data, loadingGroupTypes: isLoading }
}

export const useGetPreview = () => {
  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: Partial<IPreviewRequest>) =>
      reportsService.previewReport(data),
    onError: onQueryError,
  })
  return { mutate, getting: isLoading }
}
export function usePostPreview(record: Partial<IPreviewRequest>) {
  const { data, isLoading } = useQuery<IBaseRes<IReportViewPreview>>({
    queryFn: async () => reportsService.previewReport(record),
    queryKey: [REPORTS_ENDPOINTS.VIEW_PREVIEW, record],
    enabled: !!record,
  })
  return { data: data?.data, getting: isLoading }
}

export const useGetEmployeeTypes = () => {
  const { data, isLoading } = useQuery<IBaseRes<IEmployeeScope[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_EMPLOYEE_SCOPES],
    queryFn: async () => commonService.getEmployeeScopes(),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })

  return {
    employeeTypes: data,
    gettingTypes: isLoading,
  }
}
export const useGetRequestTypes = () => {
  const { data, isLoading } = useQuery<IBaseRes<EmployeeTypes[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_EMPLOYEE_REQUEST_TYPES],
    queryFn: async () => commonService.getEmployeeRequestTypes(),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })

  return {
    requestTypes: data,
    gettingRequestTypes: isLoading,
  }
}
